import { gql } from "@apollo/client";
import { createPagePagingActions, SortDirection, TableQuery, useTableQuery, useTableQueryPaging, useTableQuerySort } from "@comet/admin";
import { Table as AdminTable } from "@comet/admin/lib/table/Table";
import { Dialog, IconButton } from "@mui/material";
import { ContentWrapper, FormattedDateWidthDateTime, useMemoizedTableVariables } from "@src/components";
import LogInformationDetails from "@src/domain/users/edit/LogsInformationDetails";
import {
    type GQLUserAdminQuery,
    type GQLUserLogFragmentFragment,
    type GQLUserLogsAdminQuery,
    type GQLUserLogsAdminQueryVariables,
} from "@src/graphql.generated";
import ArrowRight from "@src/icons/ArrowRight";
import { type FC, type PropsWithChildren, useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";

const userLogsQuery = gql`
    fragment UserLogFragment on LogUserLogin {
        id
        data
        message
        severityLevel
        createdAt
        userId
    }

    query UserLogsAdmin($userId: ID!, $page: Int, $size: Int, $sort: SortInput) {
        userLogsAdmin(userId: $userId, page: $page, size: $size, sort: $sort) {
            nodes {
                ...UserLogFragment
            }
            totalCount
            totalPages
            nextPage
            previousPage
        }
    }
`;

const StyledDialog = styled(Dialog)`
    & .MuiDialog-paperWidthSm {
        max-width: 1000px;
    }
`;

const LogsTable: FC<PropsWithChildren<GQLUserAdminQuery>> = ({ userAdmin }) => {
    const intl = useIntl();
    const pagingApi = useTableQueryPaging(1);
    const sortApi = useTableQuerySort({ columnName: "createdAt", direction: SortDirection.DESC });
    const addAdditionalQueryVariablesFn = useCallback((): GQLUserLogsAdminQueryVariables => ({ userId: userAdmin.id, size: 5 }), [userAdmin.id]);
    const [currentLog, setCurrentLog] = useState<undefined | GQLUserLogFragmentFragment>(undefined);

    function handleDialogClose() {
        setCurrentLog(undefined);
    }

    const memoizedVariables = useMemoizedTableVariables<GQLUserLogsAdminQueryVariables>({ pagingApi, sortApi, addAdditionalQueryVariablesFn });

    const { tableData, loading, error, api } = useTableQuery<GQLUserLogsAdminQuery, GQLUserLogsAdminQueryVariables>()(userLogsQuery, {
        resolveTableData: ({ userLogsAdmin }) => ({
            data: userLogsAdmin.nodes,
            totalCount: userLogsAdmin.totalCount,
            pagingInfo: createPagePagingActions(pagingApi, userLogsAdmin),
        }),
        variables: memoizedVariables,
    });

    return (
        <>
            {currentLog && (
                <StyledDialog open={!!currentLog} onClose={handleDialogClose}>
                    <LogInformationDetails data={currentLog} />
                </StyledDialog>
            )}

            <ContentWrapper title={intl.formatMessage({ id: "reviermanager.user.logs", defaultMessage: "Protokoll" })}>
                <TableQuery api={api} loading={loading} error={error}>
                    {tableData && (
                        <AdminTable
                            {...tableData}
                            sortApi={sortApi}
                            rowName={intl.formatMessage(
                                {
                                    id: "reviermanager.pages.logs",
                                    defaultMessage: "{count, plural, one {Protokoll} other {Protokolle}}",
                                },
                                { count: tableData.totalCount },
                            )}
                            columns={[
                                {
                                    name: "createdAt",
                                    sortable: true,
                                    header: <FormattedMessage id="reviermanager.logs.createdat" defaultMessage="Erstellt am" />,
                                    render: ({ createdAt }) => <FormattedDateWidthDateTime value={createdAt} />,
                                },
                                {
                                    name: "severityLevel",
                                    header: <FormattedMessage id="reviermanager.logs.severity" defaultMessage="Typ" />,
                                    render: ({ severityLevel }) => severityLevel,
                                },
                                {
                                    name: "message",
                                    header: <FormattedMessage id="reviermanager.logs.message" defaultMessage="Nachricht" />,
                                    render: ({ message }) => message,
                                },

                                {
                                    name: "actions",
                                    render: (row) => (
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setCurrentLog(row);
                                            }}
                                        >
                                            <ArrowRight color="primary" />
                                        </IconButton>
                                    ),
                                },
                            ]}
                        />
                    )}
                </TableQuery>
            </ContentWrapper>
        </>
    );
};

export default LogsTable;
